import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { startLoadingIntentionStatistics } from '../../app/actions/statisticActions';
import NavbarIconsGenericComponent from '../generic-nav-bar/NavbarIconsGenericComponent';

const MODULE_KEYS = [
  'technology',
  'alternative',
  'experience',
  'innovation',
  'assistance',
  'financial',
  'vision',
  'regulation',
];

const ModuleBarChart = ({ rows, valueKey, moduleLabel }) => {
  const max = Math.max(1, ...rows.map((r) => r[valueKey] || 0));
  return (
    <div>
      {rows.map((row) => {
        const value = row[valueKey] || 0;
        const pct = Math.round((value / max) * 100);
        return (
          <div key={row.module_key} className="d-flex align-items-center mb-2">
            <div className="col-md-3 small pe-2">{moduleLabel(row.module_key)}</div>
            <div className="flex-grow-1">
              <div className="progress" style={{ height: '1.4rem' }}>
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${pct}%` }}
                  aria-valuenow={value}
                  aria-valuemin={0}
                  aria-valuemax={max}
                >
                  {value}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const RankingTable = ({ rows, emptyLabel, t, moduleLabel }) => (
  <div className="table-responsive">
    <table className="table table-striped table-hover align-middle">
      <thead>
        <tr>
          <th>#</th>
          <th>{t('semanticSearch.stats.col.module', { defaultValue: 'Módulo' })}</th>
          <th>{t('semanticSearch.stats.col.record', { defaultValue: 'Registro' })}</th>
          <th>{t('semanticSearch.stats.col.count', { defaultValue: 'Conteo' })}</th>
        </tr>
      </thead>
      <tbody>
        {(!rows || rows.length === 0) && (
          <tr>
            <td colSpan={4} className="text-muted">{emptyLabel}</td>
          </tr>
        )}
        {(rows || []).map((row, index) => (
          <tr key={`${row.module_key}-${row.target_reference_id}-${index}`}>
            <td>{index + 1}</td>
            <td>{moduleLabel(row.module_key)}</td>
            <td className="small">
              #{row.target_reference_id}
              {row.name ? ` — ${row.name}` : ''}
              {row.code ? ` (${row.code})` : ''}
            </td>
            <td>{row.count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ListStatisticsIntentions = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { intentionsStats, intentionsLoading } = useSelector((state) => state.statistic);
  const [filters, setFilters] = useState({
    from: '',
    to: '',
    module_key: '',
  });

  const load = (nextFilters) => {
    dispatch(startLoadingIntentionStatistics(nextFilters || filters));
  };

  useEffect(() => {
    load(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onApply = (e) => {
    e.preventDefault();
    load(filters);
  };

  const moduleLabel = (key) =>
    t(`semanticSearch.module.${key}`, { defaultValue: key || '—' });

  const demand = intentionsStats?.demandByModule || [];
  const empty = t('semanticSearch.stats.empty', { defaultValue: 'No hay datos en este periodo.' });

  return (
    <>
      <NavbarIconsGenericComponent componentName="listStatistics" />
      <div className="container mt-4">
        <h2>{t('semanticSearch.stats.title', { defaultValue: 'Intenciones y soporte' })}</h2>
        <p className="text-muted">
          {t('semanticSearch.stats.subtitle', {
            defaultValue: 'Demanda por módulo y fichas más vistas o pedidas. Sin correos ni teléfonos.',
          })}
        </p>

        <form className="row g-2 align-items-end mb-4" onSubmit={onApply}>
          <div className="col-md-3">
            <label className="form-label">{t('semanticSearch.admin.from', { defaultValue: 'Desde' })}</label>
            <input
              type="date"
              className="form-control"
              value={filters.from}
              onChange={(e) => setFilters({ ...filters, from: e.target.value })}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">{t('semanticSearch.admin.to', { defaultValue: 'Hasta' })}</label>
            <input
              type="date"
              className="form-control"
              value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value })}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">{t('semanticSearch.admin.module', { defaultValue: 'Módulo' })}</label>
            <select
              className="form-select"
              value={filters.module_key}
              onChange={(e) => setFilters({ ...filters, module_key: e.target.value })}
            >
              <option value="">{t('semanticSearch.admin.event.all', { defaultValue: 'Todos' })}</option>
              {MODULE_KEYS.map((k) => (
                <option key={k} value={k}>{moduleLabel(k)}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <button type="submit" className="btn btn-primary" disabled={intentionsLoading}>
              {t('semanticSearch.admin.apply', { defaultValue: 'Aplicar filtros' })}
            </button>
          </div>
        </form>

        {intentionsLoading && (
          <p className="text-muted">{t('semanticSearch.admin.loading', { defaultValue: 'Cargando…' })}</p>
        )}

        <h4 className="mt-3">{t('semanticSearch.stats.funnelTitle', { defaultValue: 'Embudo' })}</h4>
        <p className="small text-muted">
          {t('semanticSearch.stats.funnelHint', {
            defaultValue: 'Búsquedas → ficha del panel → comparar → puente → vista destino → soporte.',
          })}
        </p>
        <div className="row mb-4">
          {['searches', 'panel_views', 'compares', 'bridges', 'record_views', 'supports'].map((key) => (
            <div className="col-md-2" key={key}>
              <div className="border rounded p-2 text-center">
                <div className="fw-bold">{intentionsStats?.funnel?.[key] || 0}</div>
                <div className="small">{key}</div>
              </div>
            </div>
          ))}
        </div>

        <h4 className="mt-3">{t('semanticSearch.stats.panelTitle', { defaultValue: 'Aperturas de ficha (panel)' })}</h4>
        <ModuleBarChart rows={demand} valueKey="panel_count" moduleLabel={moduleLabel} />

        <h4 className="mt-3">{t('semanticSearch.stats.compareTitle', { defaultValue: 'Comparaciones' })}</h4>
        <ModuleBarChart rows={demand} valueKey="compare_count" moduleLabel={moduleLabel} />

        {(intentionsStats?.daily || []).length > 0 && (
          <>
            <h4 className="mt-3">{t('semanticSearch.stats.dailyTitle', { defaultValue: 'Por día' })}</h4>
            <div className="table-responsive mb-3">
              <table className="table table-sm">
                <thead><tr><th>Día</th><th>Evento</th><th>Conteo</th></tr></thead>
                <tbody>
                  {intentionsStats.daily.map((row, i) => (
                    <tr key={`${row.day}-${row.event_type}-${i}`}>
                      <td>{row.day}</td>
                      <td>{row.event_type}</td>
                      <td>{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <h4 className="mt-3">{t('semanticSearch.stats.demandTitle', { defaultValue: 'Pedidos de soporte por módulo' })}</h4>
        <p className="small text-muted">
          {t('semanticSearch.stats.demandHint', { defaultValue: 'Capa 3: cuántos contactos pidieron cada destino.' })}
        </p>
        <ModuleBarChart rows={demand} valueKey="support_count" moduleLabel={moduleLabel} />

        <h4 className="mt-4">{t('semanticSearch.stats.topRequestedTitle', { defaultValue: 'Fichas más pedidas' })}</h4>
        <RankingTable
          rows={intentionsStats?.topRequested}
          emptyLabel={empty}
          t={t}
          moduleLabel={moduleLabel}
        />

        <h4 className="mt-4">{t('semanticSearch.stats.topViewedTitle', { defaultValue: 'Fichas más vistas' })}</h4>
        <RankingTable
          rows={intentionsStats?.topViewed}
          emptyLabel={empty}
          t={t}
          moduleLabel={moduleLabel}
        />

        <h4 className="mt-4">{t('semanticSearch.stats.searchTitle', { defaultValue: 'De la búsqueda a los destinos' })}</h4>
        <p className="small text-muted">
          {t('semanticSearch.stats.searchHint', { defaultValue: 'Capa 1 cruzada con pedidos: qué texto llevó a qué ficha.' })}
        </p>
        <div className="table-responsive">
          <table className="table table-striped table-hover align-middle">
            <thead>
              <tr>
                <th>{t('semanticSearch.stats.col.search', { defaultValue: 'Texto buscado' })}</th>
                <th>{t('semanticSearch.stats.col.module', { defaultValue: 'Módulo' })}</th>
                <th>{t('semanticSearch.stats.col.record', { defaultValue: 'Registro' })}</th>
                <th>{t('semanticSearch.stats.col.count', { defaultValue: 'Conteo' })}</th>
              </tr>
            </thead>
            <tbody>
              {(!intentionsStats?.searchToTargets || intentionsStats.searchToTargets.length === 0) && (
                <tr>
                  <td colSpan={4} className="text-muted">{empty}</td>
                </tr>
              )}
              {(intentionsStats?.searchToTargets || []).map((row, index) => (
                <tr key={`${row.searchinfo_id}-${row.module_key}-${row.target_reference_id}-${index}`}>
                  <td className="small">{row.search_text || '—'}</td>
                  <td>{moduleLabel(row.module_key)}</td>
                  <td className="small">
                    #{row.target_reference_id}
                    {row.name ? ` — ${row.name}` : ''}
                  </td>
                  <td>{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default ListStatisticsIntentions;
